import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { setIsNewTask, fetchSortedWorkItems, setWorkItems, setUpdatedWorkItems, setTabList } from './tasks-reducer'
import { WorkItemType } from '../../api/projects-api'
import { AppRootState } from '../../app/store'
import { WorkItem } from './WorkItem'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from "react-router-dom"
import { SearchField } from './SearchField'
import { checkAutoLogin, getUserData } from '../Login/auth-reducer'
import { getLocation, setCurrentLocation } from '../../app/cookies'
import { Sorting } from '../common/Sorting'

export const WorkItemsList = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const project: HistoryStateType = getLocation()
  const isLoggedIn = useSelector<AppRootState, boolean>(state => state.auth.isLoggedIn)
  let workItems = useSelector<AppRootState, Array<WorkItemType>>((state) => state.workItems.workItemsState[project.projectName])
  let tabList = useSelector<AppRootState, string>((state) => state.workItems.openedTabList)
  const [isSearching, setIsSearching] = useState(false)
  const [tab, setTab] = useState(tabList)

  const [sortID, setIDSorting] = useState('')
  const [sortPriority, setPrioritySorting] = useState('')
  const [sortStatus, setStatusSorting] = useState('')
  const [sortTitle, setTitleSorting] = useState('')
  const [sortCreatedDate, setCreatedDateSorting] = useState('')
  const [sortLastChangeDate, setLastChangeSorting] = useState('')
  const [sort, setSort] = useState('')
  const [sortMode, setSortMode] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    setCurrentLocation(`\\ Projects \\ ${project.projectName}`)
    let token = checkAutoLogin(dispatch, true)
    if (token === null && isLoggedIn === false) {
      return navigate('/login')
    }
    dispatch(getUserData())
    tabList === 'all' && dispatch(fetchSortedWorkItems({ projectName: project.projectName, projectId: project.projectId, sort, sortMode, query }))
  }, [isLoggedIn, sort, sortMode, query])

  if (!workItems) {
    workItems = [] as Array<WorkItemType>
  }

  const syncSorting = (func: any) => {
    const funcArray = [setIDSorting, setPrioritySorting, setStatusSorting, setTitleSorting, setCreatedDateSorting, setLastChangeSorting]
    const sortArray = funcArray.filter((el) => el !== func);
    return sortArray.forEach((el) => el(''));
  };

  console.log('QUERY IN COMPONENT', query)

  const handleSorting = useCallback((sortFactor: string, column: string) => {
    setSort(column)
    if (sortFactor === 'asc') {
      setSortMode('asc')
    } else if (sortFactor === 'desc') {
      setSortMode('desc')
    } else {
      setSortMode('')
    }

    switch (column) {
      case 'System.Id': {
        setIDSorting(sortFactor)
        syncSorting(setIDSorting)
        return
      }
      case 'Microsoft.VSTS.Common.Priority': {
        setPrioritySorting(sortFactor)
        syncSorting(setPrioritySorting)
        return
      }
      case 'System.State': {
        setStatusSorting(sortFactor)
        syncSorting(setStatusSorting)
        return
      }
      case 'System.Title': {
        setTitleSorting(sortFactor)
        syncSorting(setTitleSorting)
        return
      }
      case 'System.CreatedDate': {
        setCreatedDateSorting(sortFactor)
        syncSorting(setCreatedDateSorting)
        return
      }
      case 'System.ChangedDate': {
        setLastChangeSorting(sortFactor)
        syncSorting(setLastChangeSorting)
        return
      }
      default: {
        setIDSorting('')
        syncSorting(setIDSorting)
      }
    }
  }, [])

  const searchItem = useCallback((value: string) => {
    setQuery(value)
    setIsSearching(true)
  }, [])

  const onClickUpdated = () => {
    setTab('updated')
    dispatch(setTabList({ tabName: 'updated' }))
    dispatch(setUpdatedWorkItems({ projectName: project.projectName }))
  }

  const onClickAll = () => {
    setTab('all')
    dispatch(setTabList({ tabName: 'all' }))
    dispatch(fetchSortedWorkItems({ projectName: project.projectName, projectId: project.projectId, sort, sortMode, query }))
  }

  return (
    <>
      <Grid container style={{ paddingTop: '30px' }} spacing={3} alignItems="center">

        <Grid item xl={6} md={6} xs={12}>
          <SearchField searchFailed={isSearching && workItems.length === 0} searchItem={searchItem} />
        </Grid>
        <Grid item xl={6} md={6} xs={12} >
          <Button onClick={() => {
            dispatch(setIsNewTask({ isNewTask: true }))
            navigate(`/newWorkItem/`, { state: project })
          }} color='info' className='addItemButton' style={{float: 'right'}} startIcon={<AddIcon style={{ color: 'grey' }} fontSize='medium' />}>
            Add new Bug
          </Button>
        </Grid>

        <Grid className='itemsListDetails' container>
          <Grid item className={tabList === 'all' ? 'itemsListTabActive' : 'itemsListTab'}>
            <Typography onClick={onClickAll}> All </Typography>
          </Grid>
          <Grid item className={tabList === 'updated' ? 'itemsListTabActive' : 'itemsListTab'}  >
            <Typography onClick={onClickUpdated}>Updated</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ textAlign: 'center', minWidth: '60px' }}>
              #ID
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}><Sorting column='System.Id' sortFactor={sortID} callback={handleSorting} /></span>
            </TableCell>
            <TableCell >
              Title
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}><Sorting column='System.Title' sortFactor={sortTitle} callback={handleSorting} /></span>
            </TableCell>
            <TableCell style={{ textAlign: 'center', minWidth: '75px' }}>
              Priority
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}><Sorting column='Microsoft.VSTS.Common.Priority' sortFactor={sortPriority} callback={handleSorting} /></span>
            </TableCell>
            <TableCell style={{ textAlign: 'left', minWidth: '70px' }}>
              Status
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}><Sorting column='System.State' sortFactor={sortStatus} callback={handleSorting} /></span>
            </TableCell>
            <TableCell style={{ textAlign: 'center', minWidth: '95px' }}>
              Inspection
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}></span>
            </TableCell>
            <TableCell style={{ textAlign: 'center', minWidth: '65px' }}>
              Created
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}><Sorting column='System.CreatedDate' sortFactor={sortCreatedDate} callback={handleSorting} /></span>
            </TableCell>
            <TableCell style={{ textAlign: 'center', minWidth: '154px' }}>
              Date of last change
              <span style={{ cursor: 'pointer', marginLeft: '5px' }}><Sorting column='System.ChangedDate' sortFactor={sortLastChangeDate} callback={handleSorting} /></span>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workItems.map((item) => (
            <WorkItem projectId={project.projectId} projectName={project.projectName} key={item.id} id={item.id} />
          ))}
        </TableBody>
      </Table>
    </>
  )
})

export type HistoryStateType = {
  projectId: number
  projectName: string
}
